import styled, { x } from '@xstyled/styled-components';
import React, { useEffect } from 'react';
import { t } from 'ttag';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { TrainingState, useTrainingStore } from '~/training/state';
import { Button } from '~/ui/buttons';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';

const StyledPushNotification = styled.div`
  position: absolute;
  width: 90%;
  top: 40%;
  background: #ffffff99;
  border-radius: 30px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2;

  .push-notification-content {
    display: flex;
    gap: 10px;

    img {
      border-radius: 50%;
    }

    .push-notification-header {
      display: flex;
      flex-direction: column;
      line-height: 20px;

      .push-notification-header-companyName {
        font-family: system-ui;
        font-weight: 700;
      }
      .push-notification-header-time {
        font-size: 14px;
        color: var(--color-grey-dk);
      }
    }
  }

  .push-notification-button {
    padding: 10px;
    border-radius: 12px;

    &:focus-visible,
    &:focus {
      outline: 2px solid var(--color-accent-dk);
      outline-offset: 2px;
    }
    &:focus::after {
      content: '';
      display: block;
      position: absolute;
      border-radius: 25px;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border: 2px solid white;
      pointer-events: none;
    }
  }
`;

const selector = (s: TrainingState) => select(s, 'companyName', 'logoUrl');

const mediaSelector = (s: MediaPlayerStore) => select(s, 'status', 'progress');

export const PushNotification: React.FC<{
  showLockScreen: boolean;
  setShowLockScreen: (state: boolean) => void;
  name?: string;
  content: string;
  picture?: {
    id: string;
    mediaUrl: string;
    name?: string;
    __typename: string;
  };
}> = ({ showLockScreen, setShowLockScreen, name, content, picture }) => {
  const { companyName, logoUrl } = useTrainingStore(selector);
  const { addBlocker, removeBlocker } = useSequenceStore();
  const { status } = useMediaPlayerStore(mediaSelector, shallow);

  useEffect(
    () =>
      showLockScreen ? addBlocker('lockscreen') : removeBlocker('lockscreen'),
    [showLockScreen]
  );

  let displayedName = name || companyName;

  if (displayedName.length > 14)
    displayedName = displayedName.slice(0, 14) + '...';

  return (
    <StyledPushNotification>
      <x.div className="push-notification-content">
        <x.img
          width={50}
          height={50}
          src={picture?.mediaUrl || logoUrl}
          alt={picture?.name || ''}
        />
        <x.div className="push-notification-header">
          <x.span className="push-notification-header-companyName">
            {displayedName}
          </x.span>
          <x.span className="push-notification-header-time">{t`6 min ago`}</x.span>
        </x.div>
      </x.div>
      <x.div dangerouslySetInnerHTML={{ __html: content }} />
      <Button
        disabled={!(status === 'nomedia' || status === 'done')}
        tabIndex={0}
        onClick={() => setShowLockScreen(false)}
        className="push-notification-button"
      >{t`Open`}</Button>
    </StyledPushNotification>
  );
};
